import { configAtom, ConfigType, currentLanguageAtom, PromptTranslation, store } from '@app/config-builder/store';
import { ApiAppParams } from '@typings/apiApp';
import { merge, set, unset } from 'lodash';
import { defaultTranslations } from '@/modules/common';


export const updateConfigByKey = (path: string, value: any) => {
  const prev = store.get(configAtom);
  if (!prev || !path) {
    return;
  }

  const copy = structuredClone(prev);
  if (value === undefined) {
    unset(copy, path);
  } else {
    set(copy, path, value);
  }
  store.set(configAtom, copy);
};

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const fetchConfig = async (tenant: string, env: string = 'dev') => {
  const formattedTenant = tenant.replace(/_/g, '-').toLowerCase();
  const url = `https://storage.googleapis.com/saywhatt-chat/${env}/latest/config/${formattedTenant}/builder-config.json?v=${Date.now()}`;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(url);

      if (response.ok) {
        return await response.json();
      }

      if (response.status === 404) {
        return null;
      }
    } catch {
      // network error: fall through to retry delay
    }

    if (attempt < MAX_RETRIES) {
      await delay(Math.pow(2, attempt) * BASE_DELAY_MS);
    }
  }

  console.error(`[SAYWHATT]: all ${MAX_RETRIES} fetch config retries exhausted for tenant "${tenant}"`);
  return null;
};

export const getPromptsConfig = (prompts: ConfigType['prompts'], promptsTranslation: PromptTranslation[]) => {
  if (!promptsTranslation || promptsTranslation.length === 0) {
    return [];
  }
  const sortedPromptsTranslation = promptsTranslation
    .toSorted((a, b) => a.id - b.id)
    .filter((promptTranslation) => promptTranslation.caption);
  return sortedPromptsTranslation.map((promptTranslation) => {
    const { iconUrl } = prompts?.find((prompt) => prompt.id === promptTranslation.id) ?? {};
    return {
      iconUrl,
      caption: promptTranslation.caption,
    };
  });
};

const buildSaywhattWidgetPayload = (config: ConfigType) => {
  const currentLanguage = store.get(currentLanguageAtom);
  const { translations, theme, themeOverrides, ...restConfig } = config;
  const translationsSet = {
    ...defaultTranslations[currentLanguage],
    ...translations?.[currentLanguage],
  } as (typeof defaultTranslations)['en'] & {
    prompts?: PromptTranslation[];
  };
  const promptsTranslation = translationsSet?.prompts ?? [];
  const promptsConfig = getPromptsConfig(config.prompts, promptsTranslation);

  return {
    ...restConfig,
    theme: merge({}, theme, themeOverrides ?? {}),
    translations: translationsSet,
    prompts: promptsConfig,
  } as ApiAppParams;
};

export const initSaywhattWidget = (config: ConfigType) => {
  const payload = buildSaywhattWidgetPayload(config);
  window.sayWhattChat?.init(payload);
};

export const updateSaywhattWidget = (config: ConfigType) => {
  const payload = buildSaywhattWidgetPayload(config);
  window.sayWhattChat?.updateParams(payload);
};
